/**
 * Quantity Break
 * - Désactive les champs quantité/id du formulaire principal pour qu'ils soient remplacés par ceux de l'offre sélectionnée (pas additionnés).
 * - Offre non sélectionnée : ses inputs désactivés. Offre sélectionnée : ses inputs actifs.
 * - Synchronise les variantes au changement de variante page : par ID si pas de base_product, par noms d'options (option1/2/3) si base_product.
 * - Met à jour le bloc de prix principal avec le prix de l'offre sélectionnée.
 * - Met à jour le badge promo dans .product-title__discount-badge (texte + copy button) à partir des datasets de la card sélectionnée.
 * - Met à jour le sélecteur de dimensions avec les prix du produit sélectionné (Victoria/Victoria+).
 * - Change l'image principale de la galerie selon le produit sélectionné.
 * - Si data-redirect-to-product : un clic sur une offre d'un autre produit ouvre sa fiche (?qb=handle&variant=).
 */
if (!customElements.get('quantity-break')) {
  customElements.define(
    'quantity-break',
    class QuantityBreak extends HTMLElement {
      connectedCallback() {
        this.disableMainFormQuantityInputs();

        this.querySelectorAll('.quantity-break[data-offer-index]').forEach((card) => {
          const wrapper = card.querySelector('.quantity-break_price-wrapper');
          if (wrapper?.dataset.initialPrice) {
            card.dataset.price = wrapper.dataset.initialPrice;
            if (wrapper.dataset.initialCompareAt) card.dataset.compareAt = wrapper.dataset.initialCompareAt;
          }
        });

        this.syncInputsState();
        this.syncWithCurrentVariant();

        this.querySelectorAll('.quantity-break').forEach((quantityBreak) => {
          quantityBreak.addEventListener('click', () => this.selectOffer(quantityBreak));
          quantityBreak.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.selectOffer(quantityBreak);
            }
          });
        });

        this.subscribeToVariantChange();
        this.initCardVariantSelectors();
        this.syncMainPriceBlock();
        this.syncDimensionsSelector();
        this.syncGalleryFeaturedImage();
      }

      /**
       * Synchronise toutes les cartes avec le variant actuellement sélectionné sur la page
       * Utilisé au chargement initial pour s'assurer que toutes les offres affichent les bons prix
       */
      syncWithCurrentVariant() {
        let currentVariant = null;

        const sectionRoot = this.closest('.shopify-section') || document;
        const dimensionsSelector = sectionRoot.querySelector('select[name^="options"]');
        
        if (dimensionsSelector) {
          const selectedOption = dimensionsSelector.options[dimensionsSelector.selectedIndex];
          const variantId = selectedOption?.dataset?.variantId;
          
          if (variantId) {
            const cards = this.querySelectorAll('.quantity-break[data-offer-index]');
            
            for (const card of cards) {
              const scriptEl = card.querySelector('script[data-offer-variants]');
              
              if (scriptEl?.textContent?.trim()) {
                try {
                  const variants = JSON.parse(scriptEl.textContent.trim());
                  currentVariant = variants.find(v => v.id.toString() === variantId);
                  
                  if (currentVariant) {
                    break;
                  }
                } catch (err) {
                  console.error('Quantity break: error parsing variants', err);
                }
              }
            }
          }
        }

        if (!currentVariant) {
          const formId = this.dataset.formId;
          if (formId) {
            const form = document.getElementById(formId);
            const variantIdInput = form?.querySelector('input[name="id"]');
            
            if (variantIdInput?.value) {
              const variantId = variantIdInput.value;
              const cards = this.querySelectorAll('.quantity-break[data-offer-index]');
              
              for (const card of cards) {
                const scriptEl = card.querySelector('script[data-offer-variants]');
                
                if (scriptEl?.textContent?.trim()) {
                  try {
                    const variants = JSON.parse(scriptEl.textContent.trim());
                    currentVariant = variants.find(v => v.id.toString() === variantId);
                    if (currentVariant) break;
                  } catch (err) {
                    // Continue
                  }
                }
              }
            }
          }
        }

        if (currentVariant) {
          this.onVariantChange({ detail: { variant: currentVariant } });
        }
      }

      initCardVariantSelectors() {
        if (this.dataset.variantPickerMode !== 'inside_cards') return;

        this.querySelectorAll('.quantity-break-variant-picker-in-card').forEach((container) => {
          const card = container.closest('.quantity-break[data-offer-index]');
          const scriptEl = card?.querySelector('script[data-offer-variants]');
          const selects = container.querySelectorAll('.quantity-break-card-option');
          if (!card || !scriptEl?.textContent?.trim() || !selects.length) return;

          let variants;
          try {
            variants = JSON.parse(scriptEl.textContent.trim());
          } catch (err) {
            return;
          }
          if (!Array.isArray(variants) || variants.length === 0) return;

          const onCardOptionChange = () => {
            const values = Array.from(selects).map((s) => (s.value || '').trim());
            const variant =
              variants.find(
                (v) =>
                  (v.option1 || '').toString().trim() === (values[0] || '') &&
                  (v.option2 || '').toString().trim() === (values[1] || '') &&
                  (v.option3 || '').toString().trim() === (values[2] || '')
              ) || variants[0];

            const qty = parseInt(card.dataset.quantityNumber || '1', 10) || 1;
            const discountVal = card.dataset.discountValue
              ? this.parseDiscountValueToCents(card.dataset.discountValue)
              : null;

            const price = variant.price;
            const compareAt = variant.compare_at_price || variant.price;

            const { price: finalPrice, compareAt: finalCompareAt } = this.applyDiscount(
              price * qty,
              compareAt * qty,
              discountVal
            );

            this.updateOfferCard(card, variant, finalPrice, finalCompareAt, qty);
            card.dataset.price = String(finalPrice);
            card.dataset.compareAt = String(finalCompareAt);
            this.updateOfferDisabledState(card, variant.available);

            this.syncMainPriceBlock();
          };

          selects.forEach((select) => {
            select.addEventListener('change', onCardOptionChange);
          });
        });
      }

      disconnectedCallback() {
        if (this._variantChangeUnsubscriber) this._variantChangeUnsubscriber();
        const form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
        if (form) form.removeEventListener('variant:change', this._boundVariantChange);
      }

      subscribeToVariantChange() {
        this._boundVariantChange = (e) => this.onVariantChange(e);

        const formId = this.dataset.formId;
        if (formId) {
          const form = document.getElementById(formId);
          if (form) form.addEventListener('variant:change', this._boundVariantChange);
        }

        if (typeof theme !== 'undefined' && theme.pubsub) {
          this._variantChangeUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
            const sectionId = this.dataset.sectionId;
            const productId = this.dataset.productId;
            if (event.data?.sectionId === sectionId && event.data?.productId?.toString() === productId?.toString()) {
              this.onVariantChange({ detail: { variant: event.data.variant } });
            }
          });
        }
      }

      onVariantChange(e) {
        const variant = e.detail?.variant;
        if (!variant) return;

        const cards = this.querySelectorAll('.quantity-break[data-offer-index]');
        for (const card of cards) {
          const result = this.resolveVariantForOffer(card, variant);
          if (!result) continue;

          const qty = parseInt(card.dataset.quantityNumber || '1', 10) || 1;
          const discountVal = card.dataset.discountValue
            ? this.parseDiscountValueToCents(card.dataset.discountValue)
            : null;

          const { price: finalPrice, compareAt: finalCompareAt } = this.applyDiscount(
            result.price * qty,
            (result.compare_at_price || result.price) * qty,
            discountVal
          );

          this.updateOfferCard(card, result.variant, finalPrice, finalCompareAt, qty);
          card.dataset.price = String(finalPrice);
          card.dataset.compareAt = String(finalCompareAt);
          this.updateOfferDisabledState(card, result.variant.available);
          this.syncCardOptionSelects(card, result.variant);
        }

        this.syncMainPriceBlock();
        this.syncDimensionsSelector();
      }

      parseDiscountValueToCents(str) {
        if (!str || typeof str !== 'string') return 0;
        const num = parseFloat(str.replace(',', '.').replace(/\s/g, ''));
        return isNaN(num) ? 0 : Math.round(num * 100);
      }

      applyDiscount(priceCents, compareAtCents, discountValueCents) {
        let price = priceCents;
        if (discountValueCents != null && discountValueCents > 0) {
          price = Math.max(0, priceCents - discountValueCents);
        }
        return { price, compareAt: compareAtCents };
      }

      /**
       * Normalise une valeur pour comparaison d'ensemble (trim, lowercase, collapse spaces).
       * Retourne un tableau trié des valeurs non vides pour comparaison indépendante de l'ordre.
       */
      _variantOptionValuesSet(variant) {
        const norm = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
        return [
          norm(variant.option1),
          norm(variant.option2),
          norm(variant.option3)
        ].filter(Boolean).sort();
      }

      /**
       * Compare deux valeurs d'option. Strict : égalité exacte après normalisation
       * (trim, lowercase, espaces réduits/supprimés). Pas d'utilisation de includes()
       * pour éviter les faux positifs (ex. "80x200cm" ne doit pas matcher "180x200cm").
       */
      _variantValuesMatch(a, b) {
        const n = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ').trim();
        const na = n(a);
        const nb = n(b);
        if (na === nb) return true;
        return na.replace(/\s/g, '') === nb.replace(/\s/g, '');
      }

      resolveVariantForOffer(card, mainVariant) {
        const baseHandle = (card.dataset.baseProductHandle || '').trim();

        if (!baseHandle) {
          return {
            variant: mainVariant,
            price: mainVariant.price,
            compare_at_price: mainVariant.compare_at_price
          };
        }

        const scriptEl = card.querySelector('script[data-offer-variants]');
        if (!scriptEl?.textContent?.trim()) return null;

        let variants;
        try {
          variants = JSON.parse(scriptEl.textContent.trim());
        } catch (err) {
          console.warn('Quantity break: invalid variants JSON', err);
          return null;
        }
        if (!Array.isArray(variants) || variants.length === 0) return null;

        const mainSet = this._variantOptionValuesSet(mainVariant);

        let match = variants.find((v) => {
          const offerSet = this._variantOptionValuesSet(v);
          const lengthOk = mainSet.length === offerSet.length;
          return lengthOk && mainSet.every((mainVal, i) => mainVal === offerSet[i] || this._variantValuesMatch(mainVal, offerSet[i]));
        });

        if (!match) {
          match = variants.find((v) => {
            const offerSet = this._variantOptionValuesSet(v);
            if (mainSet.length !== offerSet.length) return false;
            return mainSet.every((mainVal) =>
              offerSet.some((offerVal) => this._variantValuesMatch(mainVal, offerVal))
            );
          });
        }

        if (!match && mainSet.length > 0) {
          let bestScore = -1;
          variants.forEach((v) => {
            const offerSet = this._variantOptionValuesSet(v);
            const score = mainSet.filter((mainVal) =>
              offerSet.some((offerVal) => this._variantValuesMatch(mainVal, offerVal))
            ).length;
            if (score > bestScore) bestScore = score;
          });
          match = variants.find((v) => {
            const offerSet = this._variantOptionValuesSet(v);
            const score = mainSet.filter((mainVal) =>
              offerSet.some((offerVal) => this._variantValuesMatch(mainVal, offerVal))
            ).length;
            return score === bestScore && bestScore > 0;
          }) || null;
        }

        const variant = match || variants[0];

        return {
          variant,
          price: variant.price,
          compare_at_price: variant.compare_at_price
        };
      }

      /**
       * Synchronise les selects de variante dans la carte avec la variante.
       * Match par valeur uniquement : pour chaque select, on cherche quelle valeur
       * (option1, option2 ou option3) est présente dans ses options, sans utiliser l'ordre.
       */
      syncCardOptionSelects(card, variant) {
        const container = card.querySelector('.quantity-break-variant-picker-in-card');
        if (!container) return;

        const variantValues = [
          (variant.option1 || '').toString().trim(),
          (variant.option2 || '').toString().trim(),
          (variant.option3 || '').toString().trim()
        ].filter(Boolean);

        container.querySelectorAll('.quantity-break-card-option').forEach((select) => {
          const optionsArray = Array.from(select.options);
          for (const variantVal of variantValues) {
            if (!variantVal) continue;
            const option = optionsArray.find((opt) => this._variantValuesMatch(opt.value, variantVal));
            if (option) {
              select.value = option.value;
              break;
            }
          }
        });
      }

      updateOfferCard(card, variant, priceCents, compareAtCents, quantityNumber) {
        const inputsContainer = card.querySelector('.quantity-break-form-inputs[data-offer-index]');
        if (!inputsContainer) return;

        const idInputs = inputsContainer.querySelectorAll('input[name="id"], input[name^="items"][name*="[id]"]');
        idInputs.forEach((input) => {
          input.value = variant.id;
        });

        const priceEl = card.querySelector('.quantity-break_price-container_price');
        const compareEl = card.querySelector('.quantity-break_price-container_compare-at-price');

        const format =
          (typeof theme !== 'undefined' && theme.settings?.moneyFormat) ||
          (typeof window.themeVariables !== 'undefined' && window.themeVariables?.settings?.moneyFormat) ||
          '{{amount}}';

        const formattedPrice =
          typeof theme !== 'undefined' && theme.Currency?.formatMoney
            ? theme.Currency.formatMoney(priceCents, format)
            : `${(priceCents / 100).toFixed(2)}`;

        if (priceEl) priceEl.textContent = formattedPrice.replace(/[,.]00$/, '');

        if (compareEl) {
          if (compareAtCents > priceCents) {
            compareEl.textContent = (
              typeof theme !== 'undefined' && theme.Currency?.formatMoney
                ? theme.Currency.formatMoney(compareAtCents, format)
                : `${(compareAtCents / 100).toFixed(2)}`
            ).replace(/[,.]00$/, '');
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }

        const unitPriceEl = card.querySelector('.quantity-break_price-container_unit-price');
        if (unitPriceEl && quantityNumber > 0) {
          const unitCents = Math.round(priceCents / quantityNumber);
          const unitFormatted =
            typeof theme !== 'undefined' && theme.Currency?.formatMoney
              ? theme.Currency.formatMoney(unitCents, format)
              : `${(unitCents / 100).toFixed(2)}`;
          const unitText = 'par article';
          unitPriceEl.textContent = `${unitFormatted.replace(/[,.]00$/, '')} ${unitText}`.trim();
        }

        const badgeEl = card.querySelector('.quantity-break_banner-next-to-price');
        if (badgeEl && badgeEl.dataset.savingsAsPercent !== 'true') {
          if (compareAtCents > priceCents) {
            const diffCents = compareAtCents - priceCents;
            const diffFormatted =
              typeof theme !== 'undefined' && theme.Currency?.formatMoney
                ? theme.Currency.formatMoney(diffCents, format)
                : `${(diffCents / 100).toFixed(2)}`;
            badgeEl.textContent = `−${diffFormatted.replace(/[,.]00$/, '')}`;
            badgeEl.hidden = false;
          } else {
            badgeEl.hidden = true;
          }
        }
      }

      updateOfferDisabledState(card, available) {
        if (available) {
          card.classList.remove('disabled-quantity-break');
        } else {
          card.classList.add('disabled-quantity-break');
        }
      }

      syncMainPriceBlock() {
        const selected = this.querySelector('.quantity-break.selected-quantity-break');
        if (!selected) return;

        let priceCents = selected.dataset.price ? parseInt(selected.dataset.price, 10) : null;
        let compareAtCents = selected.dataset.compareAt ? parseInt(selected.dataset.compareAt, 10) : null;

        if (priceCents == null) {
          const wrapper = selected.querySelector('.quantity-break_price-wrapper');
          if (wrapper?.dataset.initialPrice) {
            priceCents = parseInt(wrapper.dataset.initialPrice, 10);
            compareAtCents = wrapper.dataset.initialCompareAt ? parseInt(wrapper.dataset.initialCompareAt, 10) : null;
          }
        }

        if (priceCents == null) return;

        this.updateMainPriceBlock(priceCents, compareAtCents);
        this.syncDiscountBadge();
      }

      /**
       * Met à jour le badge promo header produit à partir des datasets de la card sélectionnée:
       * - selected.dataset.discountBadgeText => texte affiché (ex: "-15% avec HIVER15")
       * - selected.dataset.discountCode      => code copié (ex: "HIVER15")
       *
       * Cible DOM:
       * .product-title__discount-badge
       *   - text node avant le bouton
       *   - bouton: .product-title__copy-btn / [is="copy-button"]
       *
       * ⚠️ On n'utilise PAS innerHTML pour éviter de casser le custom element + ton script inline.
       */
      syncDiscountBadge() {
        const selected = this.querySelector('.quantity-break.selected-quantity-break');
        if (!selected) return;

        const code = (selected.dataset.discountCode || '').trim();
        const badgeText = (selected.dataset.discountBadgeText || '').trim();

        const sectionRoot = this.closest('.shopify-section') || document;
        const badge = sectionRoot.querySelector('.product-title__discount-badge');
        if (!badge) return;

        const copyBtn = badge.querySelector('.product-title__copy-btn, [is="copy-button"]');

        if (badgeText) {
          badge.classList.remove('hidden');
          badge.removeAttribute('aria-hidden');
        } else {
          badge.classList.add('hidden');
          badge.setAttribute('aria-hidden', 'true');
        }

        if (copyBtn) {
          const textNode = Array.from(badge.childNodes).find((n) => n.nodeType === Node.TEXT_NODE);
          const normalized = badgeText ? ` ${badgeText} ` : ' ';

          if (textNode) {
            textNode.textContent = normalized;
          } else {
            badge.insertBefore(document.createTextNode(normalized), copyBtn);
          }
        } else {
          badge.textContent = badgeText || '';
        }

        if (copyBtn) {
          copyBtn.dataset.text = code;
          copyBtn.setAttribute('aria-label', code ? `Copier le code promo ${code}` : 'Copier le code promo');
        }
      }

      /**
       * Met à jour le sélecteur de dimensions avec les prix du produit de l'offre sélectionnée
       */
      syncDimensionsSelector() {
        const selected = this.querySelector('.quantity-break.selected-quantity-break');
        if (!selected) return;

        const baseHandle = (selected.dataset.baseProductHandle || '').trim();
        
        if (!baseHandle) return;

        const scriptEl = selected.querySelector('script[data-offer-variants]');
        if (!scriptEl?.textContent?.trim()) return;

        let offerVariants;
        try {
          offerVariants = JSON.parse(scriptEl.textContent.trim());
        } catch (err) {
          console.warn('Quantity break: cannot parse offer variants', err);
          return;
        }

        if (!Array.isArray(offerVariants) || offerVariants.length === 0) return;

        const sectionRoot = this.closest('.shopify-section') || document;
        const dimensionsSelector = sectionRoot.querySelector('select[name^="options"]');
        
        if (!dimensionsSelector) return;

        const format =
          (typeof theme !== 'undefined' && theme.settings?.moneyFormat) ||
          (typeof window.themeVariables !== 'undefined' && window.themeVariables?.settings?.moneyFormat) ||
          '{{amount}}';

        Array.from(dimensionsSelector.options).forEach((option) => {
          const optionValue = option.value.trim();
          
          const matchingVariant = offerVariants.find(v => {
            const v1 = (v.option1 || '').toString().trim();
            const v2 = (v.option2 || '').toString().trim();
            const v3 = (v.option3 || '').toString().trim();
            
            return this._variantValuesMatch(v1, optionValue) || 
                   this._variantValuesMatch(v2, optionValue) || 
                   this._variantValuesMatch(v3, optionValue);
          });

          if (matchingVariant) {
            const parts = option.textContent.split(' - ');
            const dimensionName = parts[0].trim();
            
            const newPrice = typeof theme !== 'undefined' && theme.Currency?.formatMoney
              ? theme.Currency.formatMoney(matchingVariant.price, format)
              : `${(matchingVariant.price / 100).toFixed(2)} €`;
            
            option.textContent = `${dimensionName} - ${newPrice.replace(/[,.]00$/, '')}`;
          }
        });
      }

      /**
       * Change l'image principale de la galerie avec celle du produit sélectionné
       */
      syncGalleryFeaturedImage() {
        const selected = this.querySelector('.quantity-break.selected-quantity-break');
        if (!selected) return;

        const featuredImageUrl = selected.dataset.featuredImage;
        const featuredImageAlt = selected.dataset.featuredImageAlt || '';
        const featuredImageWidth = selected.dataset.featuredImageWidth || '1000';
        const featuredImageHeight = selected.dataset.featuredImageHeight || '1000';

        if (!featuredImageUrl) return;

        const sectionRoot = this.closest('.shopify-section') || document;
        const mediaGallery = sectionRoot.querySelector('media-gallery');
        
        if (!mediaGallery) return;

        // Image principale dans le slider
        const firstMediaInSlider = mediaGallery.querySelector('.product__media-list .product__media:first-child img');
        
        if (firstMediaInSlider) {
          firstMediaInSlider.src = featuredImageUrl;
          firstMediaInSlider.srcset = featuredImageUrl;
          firstMediaInSlider.alt = featuredImageAlt;
          firstMediaInSlider.setAttribute('width', featuredImageWidth);
          firstMediaInSlider.setAttribute('height', featuredImageHeight);
        }

        // Image preview sticky (desktop)
        const previewMedia = mediaGallery.querySelector('.product__preview .product__media img');
        
        if (previewMedia) {
          previewMedia.src = featuredImageUrl;
          previewMedia.srcset = featuredImageUrl;
          previewMedia.alt = featuredImageAlt;
          previewMedia.setAttribute('width', featuredImageWidth);
          previewMedia.setAttribute('height', featuredImageHeight);
        }

        // Première vignette (thumbnail)
        const firstThumbnail = mediaGallery.querySelector('.product__thumbnails-list button:first-child img, .product__thumbnails-list [data-target="0"] img');
        
        if (firstThumbnail) {
          const thumbnailUrl = featuredImageUrl.replace(/width=\d+/, 'width=200');
          firstThumbnail.src = thumbnailUrl;
          firstThumbnail.srcset = thumbnailUrl;
          firstThumbnail.alt = featuredImageAlt;
        }
      }

      updateMainPriceBlock(priceCents, compareAtCents) {
        const sectionId = this.dataset.sectionId;
        const productId = this.dataset.productId;
        if (!sectionId || !productId) return;

        const block = document.getElementById(`Price-${sectionId}-${productId}`);
        if (!block) return;

        const format =
          (typeof theme !== 'undefined' && theme.settings?.moneyFormat) ||
          (typeof window.themeVariables !== 'undefined' && window.themeVariables?.settings?.moneyFormat) ||
          '{{amount}}';

        const formattedPrice =
          typeof theme !== 'undefined' && theme.Currency?.formatMoney
            ? theme.Currency.formatMoney(priceCents, format)
            : `${(priceCents / 100).toFixed(2)}`;

        const regularEl = block.querySelector('.price__regular[data-price-default], .price__regular');
        const saleEl = block.querySelector('.price__sale');

        if (regularEl) regularEl.textContent = formattedPrice.replace(/[,.]00$/, '');

        if (saleEl) {
          if (compareAtCents != null && compareAtCents > priceCents) {
            saleEl.textContent = (
              typeof theme !== 'undefined' && theme.Currency?.formatMoney
                ? theme.Currency.formatMoney(compareAtCents, format)
                : `${(compareAtCents / 100).toFixed(2)}`
            ).replace(/[,.]00$/, '');
            saleEl.hidden = false;
          } else {
            saleEl.hidden = true;
          }
        }

        const promoEl = block.querySelector('.product__price-promo.product__info-tag');
        if (promoEl) {
          const label = promoEl.dataset.pricePromoLabel || 'Économisez ';
          if (compareAtCents != null && compareAtCents > priceCents) {
            const diffCents = compareAtCents - priceCents;
            const diffFormatted =
              typeof theme !== 'undefined' && theme.Currency?.formatMoney
                ? theme.Currency.formatMoney(diffCents, format)
                : `${(diffCents / 100).toFixed(2)}`;
            promoEl.textContent = label + diffFormatted.replace(/[,.]00$/, '');
            promoEl.hidden = false;
          } else {
            promoEl.hidden = true;
          }
        }
      }

      offerRedirectUrl(card) {
        if (this.dataset.redirectToProduct !== 'true') return null;

        const offerHandle = (card.dataset.offerProductHandle || '').trim();
        const currentHandle = (this.dataset.productHandle || '').trim();
        const offerUrl = (card.dataset.offerProductUrl || '').trim();
        if (!offerUrl || !offerHandle || offerHandle === currentHandle) return null;

        let url;
        try {
          url = new URL(offerUrl, window.location.origin);
        } catch (err) {
          return null;
        }

        url.searchParams.set('qb', offerHandle);

        const variantInput = card.querySelector(
          '.quantity-break-form-inputs input[name="id"], .quantity-break-form-inputs input[name^="items"][name*="[id]"]'
        );
        if (variantInput?.value) url.searchParams.set('variant', variantInput.value);

        const current = new URL(window.location.href);
        ['preview_theme_id', 'oseid'].forEach((key) => {
          const value = current.searchParams.get(key);
          if (value) url.searchParams.set(key, value);
        });

        return url.toString();
      }

      selectOffer(quantityBreak) {
        const redirectUrl = this.offerRedirectUrl(quantityBreak);
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }

        if (quantityBreak.classList.contains('disabled-quantity-break')) return;

        this.querySelectorAll('.quantity-break').forEach((qb) => {
          qb.classList.remove('selected-quantity-break');
          qb.setAttribute('aria-pressed', 'false');
        });

        quantityBreak.classList.add('selected-quantity-break');
        quantityBreak.setAttribute('aria-pressed', 'true');

        this.syncInputsState();
        this.syncMainPriceBlock();
        this.syncDimensionsSelector();
        this.syncGalleryFeaturedImage();
      }

      disableMainFormQuantityInputs() {
        const formId = this.dataset.formId;
        if (!formId) return;

        const form = document.getElementById(formId);
        if (!form) return;

        const quantityInput = form.querySelector('input[name="quantity"]');
        const idInput = form.querySelector('input[name="id"]');

        if (quantityInput) quantityInput.disabled = true;
        if (idInput) idInput.disabled = true;
      }

      syncInputsState() {
        const selected = this.querySelector('.quantity-break.selected-quantity-break');
        const selectedIndex = selected ? selected.getAttribute('data-offer-index') : null;

        this.querySelectorAll('.quantity-break-form-inputs input[data-offer-index]').forEach((input) => {
          input.disabled = input.getAttribute('data-offer-index') !== selectedIndex;
        });
      }
    }
  );
}